import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Button,
  Card,
  DatePicker,
  Form,
  Input,
  InputNumber,
  Modal,
  Popconfirm,
  Select,
  Space,
  Table,
  Tag,
  Typography,
  message,
} from 'antd';
import dayjs, { Dayjs } from 'dayjs';
import { Plus, RefreshCw } from 'lucide-react';
import { avmApi } from '../../services/api';

const { Text } = Typography;

interface AvmSale {
  id: string;
  zoneId: string;
  source: 'NOTARIA' | 'SWOMEY';
  price: number;
  surface: number;
  pricePerM2: number;
  saleDate?: string | null;
  reference?: string | null;
  address?: string | null;
  notes?: string | null;
  createdAt: string;
  updatedAt: string;
  zone?: {
    neighborhood?: string | null;
    zone?: string | null;
    municipality?: string | null;
  } | null;
}

interface SaleFormValues {
  zoneId: string;
  source: 'NOTARIA' | 'SWOMEY';
  price: number;
  surface: number;
  saleDate?: Dayjs | null;
  reference?: string;
  address?: string;
  notes?: string;
}

const formatNumber = (value?: number | null, decimals = 0) =>
  typeof value === 'number'
    ? value.toLocaleString('es-ES', { minimumFractionDigits: decimals, maximumFractionDigits: decimals })
    : '-';

export default function AvmSalesTab() {
  const [rows, setRows] = useState<AvmSale[]>([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState('');
  const [zoneFilter, setZoneFilter] = useState<string>('');
  const [sourceFilter, setSourceFilter] = useState<'NOTARIA' | 'SWOMEY' | ''>('');
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState<AvmSale | null>(null);
  const [saving, setSaving] = useState(false);
  const [form] = Form.useForm<SaleFormValues>();

  const fetchRows = useCallback(async () => {
    setLoading(true);
    try {
      const params: Record<string, unknown> = { limit: 500 };
      if (search) params.search = search;
      if (zoneFilter) params.zoneId = zoneFilter;
      if (sourceFilter) params.source = sourceFilter;
      const { data } = await avmApi.listSales(params);
      setRows(data.data || []);
    } catch {
      message.error('No se pudo cargar el listado de ventas');
    } finally {
      setLoading(false);
    }
  }, [search, zoneFilter, sourceFilter]);

  useEffect(() => {
    fetchRows();
  }, [fetchRows]);

  const openCreate = () => {
    setEditing(null);
    form.resetFields();
    form.setFieldsValue({ source: 'NOTARIA' });
    setModalOpen(true);
  };

  const openEdit = (sale: AvmSale) => {
    setEditing(sale);
    form.setFieldsValue({
      zoneId: sale.zoneId,
      source: sale.source,
      price: sale.price,
      surface: sale.surface,
      saleDate: sale.saleDate ? dayjs(sale.saleDate) : null,
      reference: sale.reference || undefined,
      address: sale.address || undefined,
      notes: sale.notes || undefined,
    });
    setModalOpen(true);
  };

  const handleSave = async () => {
    try {
      const values = await form.validateFields();
      setSaving(true);
      const payload = {
        zoneId: values.zoneId.trim(),
        source: values.source,
        price: Number(values.price),
        surface: Number(values.surface),
        saleDate: values.saleDate ? values.saleDate.toISOString() : undefined,
        reference: values.reference || undefined,
        address: values.address || undefined,
        notes: values.notes || undefined,
      };
      if (editing) {
        await avmApi.updateSale(editing.id, payload);
        message.success('Venta actualizada');
      } else {
        await avmApi.createSale(payload);
        message.success('Venta creada');
      }
      setModalOpen(false);
      fetchRows();
    } catch (error: any) {
      if (error?.errorFields) return;
      const msg =
        error?.response?.data?.message ||
        (Array.isArray(error?.response?.data?.message) ? error.response.data.message.join(' | ') : null) ||
        'No se pudo guardar la venta';
      message.error(typeof msg === 'string' ? msg : 'Error al guardar');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (sale: AvmSale) => {
    try {
      await avmApi.deleteSale(sale.id);
      message.success('Venta eliminada');
      fetchRows();
    } catch {
      message.error('No se pudo eliminar');
    }
  };

  const columns = useMemo(
    () => [
      {
        title: 'ID_ZONA',
        dataIndex: 'zoneId',
        key: 'zoneId',
        width: 110,
        sorter: (a: AvmSale, b: AvmSale) => a.zoneId.localeCompare(b.zoneId, 'es', { numeric: true }),
        defaultSortOrder: 'ascend' as const,
      },
      {
        title: 'Barrio',
        key: 'neighborhood',
        width: 160,
        render: (_: unknown, row: AvmSale) => row.zone?.neighborhood || '-',
        sorter: (a: AvmSale, b: AvmSale) =>
          (a.zone?.neighborhood || '').localeCompare(b.zone?.neighborhood || '', 'es'),
      },
      {
        title: 'Zona',
        key: 'zone',
        width: 130,
        render: (_: unknown, row: AvmSale) => row.zone?.zone || '-',
        sorter: (a: AvmSale, b: AvmSale) =>
          (a.zone?.zone || '').localeCompare(b.zone?.zone || '', 'es'),
      },
      {
        title: 'Fuente',
        dataIndex: 'source',
        key: 'source',
        width: 110,
        render: (v: string) => (
          <Tag color={v === 'NOTARIA' ? 'gold' : 'blue'}>{v}</Tag>
        ),
      },
      {
        title: 'Fecha venta',
        dataIndex: 'saleDate',
        key: 'saleDate',
        width: 130,
        render: (v?: string | null) => (v ? dayjs(v).format('DD/MM/YYYY') : '-'),
        sorter: (a: AvmSale, b: AvmSale) =>
          (a.saleDate ? new Date(a.saleDate).getTime() : 0) -
          (b.saleDate ? new Date(b.saleDate).getTime() : 0),
      },
      {
        title: 'Precio',
        dataIndex: 'price',
        key: 'price',
        width: 140,
        render: (v: number) => `${formatNumber(v)} €`,
      },
      {
        title: 'Superficie',
        dataIndex: 'surface',
        key: 'surface',
        width: 110,
        render: (v: number) => `${formatNumber(v)} m²`,
      },
      {
        title: '€/m²',
        dataIndex: 'pricePerM2',
        key: 'pricePerM2',
        width: 130,
        render: (v: number) => `${formatNumber(v, 2)} €/m²`,
      },
      { title: 'Referencia', dataIndex: 'reference', key: 'reference', width: 150, render: (v?: string) => v || '-' },
      { title: 'Dirección', dataIndex: 'address', key: 'address', width: 220, render: (v?: string) => v || '-' },
      { title: 'Notas', dataIndex: 'notes', key: 'notes', width: 200, render: (v?: string) => v || '-' },
      {
        title: 'Acciones',
        key: 'actions',
        width: 160,
        fixed: 'right' as const,
        render: (_: unknown, row: AvmSale) => (
          <Space>
            <Button size="small" onClick={() => openEdit(row)}>
              Editar
            </Button>
            <Popconfirm
              title="¿Eliminar venta?"
              description="Se recalcularán las medias de la zona."
              okText="Sí, eliminar"
              cancelText="Cancelar"
              onConfirm={() => handleDelete(row)}
            >
              <Button size="small" danger>
                Eliminar
              </Button>
            </Popconfirm>
          </Space>
        ),
      },
    ],
    [],
  );

  return (
    <div>
      <Card size="small" style={{ marginBottom: 12 }}>
        <Space wrap style={{ width: '100%', justifyContent: 'space-between' }}>
          <Space wrap>
            <Input.Search
              allowClear
              placeholder="Buscar por ID_ZONA, referencia, dirección..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              onSearch={() => fetchRows()}
              style={{ width: 320 }}
            />
            <Input
              allowClear
              placeholder="Filtrar por ID_ZONA exacto"
              value={zoneFilter}
              onChange={(e) => setZoneFilter(e.target.value)}
              style={{ width: 200 }}
            />
            <Select
              allowClear
              placeholder="Fuente"
              value={sourceFilter || undefined}
              onChange={(v) => setSourceFilter((v as 'NOTARIA' | 'SWOMEY') || '')}
              style={{ width: 160 }}
              options={[
                { value: 'NOTARIA', label: 'NOTARÍA' },
                { value: 'SWOMEY', label: 'SWOMEY' },
              ]}
            />
            <Button icon={<RefreshCw size={14} />} onClick={fetchRows}>
              Recargar
            </Button>
          </Space>
          <Space>
            <Text type="secondary">{rows.length} ventas</Text>
            <Button type="primary" icon={<Plus size={14} />} onClick={openCreate}>
              Nueva venta
            </Button>
          </Space>
        </Space>
      </Card>

      <Table
        rowKey="id"
        loading={loading}
        columns={columns as any}
        dataSource={rows}
        pagination={{ pageSize: 50 }}
        scroll={{ x: 1700, y: 620 }}
        size="small"
      />

      <Modal
        title={editing ? 'Editar venta' : 'Nueva venta'}
        open={modalOpen}
        onCancel={() => setModalOpen(false)}
        onOk={handleSave}
        confirmLoading={saving}
        okText={editing ? 'Guardar cambios' : 'Crear venta'}
        width={720}
        destroyOnClose
      >
        <Text type="secondary">
          Al crear/editar/eliminar una venta se recalculan automáticamente las medias
          (notaria, swomey y reales total) de la zona asociada.
        </Text>
        <Form form={form} layout="vertical" style={{ marginTop: 12 }}>
          <Space wrap style={{ width: '100%' }}>
            <Form.Item
              label="ID_ZONA"
              name="zoneId"
              rules={[{ required: true, message: 'ID_ZONA obligatorio (ej: ID-001)' }]}
            >
              <Input placeholder="ID-001" style={{ width: 180 }} />
            </Form.Item>
            <Form.Item
              label="Fuente"
              name="source"
              rules={[{ required: true, message: 'Fuente obligatoria' }]}
            >
              <Select
                style={{ width: 160 }}
                options={[
                  { value: 'NOTARIA', label: 'NOTARÍA' },
                  { value: 'SWOMEY', label: 'SWOMEY' },
                ]}
              />
            </Form.Item>
            <Form.Item label="Fecha venta" name="saleDate">
              <DatePicker style={{ width: 170 }} format="DD/MM/YYYY" />
            </Form.Item>
          </Space>

          <Space wrap style={{ width: '100%' }}>
            <Form.Item
              label="Precio (€)"
              name="price"
              rules={[{ required: true, message: 'Precio obligatorio' }]}
            >
              <InputNumber min={0} style={{ width: 200 }} step={1000} />
            </Form.Item>
            <Form.Item
              label="Superficie (m²)"
              name="surface"
              rules={[{ required: true, message: 'Superficie obligatoria' }]}
            >
              <InputNumber min={0} style={{ width: 180 }} step={1} />
            </Form.Item>
          </Space>

          <Form.Item label="Referencia" name="reference">
            <Input placeholder="Referencia catastral / nº protocolo" />
          </Form.Item>
          <Form.Item label="Dirección" name="address">
            <Input placeholder="Dirección o ubicación de la propiedad" />
          </Form.Item>
          <Form.Item label="Notas" name="notes">
            <Input.TextArea rows={3} />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
}
